/* istanbul ignore file */

import { useCallback } from 'react';
import { useClient } from 'urql';

import { useIsLoggedIn } from '@order/AuthMachine';
import { useOrders } from '@order/features/orders';

import {
  OrdersV2ForReorderDocument,
  type OrdersV2ForReorderQuery,
  type OrdersV2ForReorderQueryVariables,
} from '../../graphql/ReorderOrders.generated';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A hook for fetching the user's most recent orders with pagination support.
 */
export const useReorderOrders = () => {
  const client = useClient();
  const isLoggedIn = useIsLoggedIn();

  // ─── Queries ─────────────────────────────────────────────────────────

  const ordersQuery = useCallback(
    async (input: OrdersV2ForReorderQueryVariables['input']) => {
      const queryOrders = client.query<
        OrdersV2ForReorderQuery,
        OrdersV2ForReorderQueryVariables
      >;

      return queryOrders(
        OrdersV2ForReorderDocument,
        { input },
        { requestPolicy: 'network-only' },
      ).toPromise();
    },
    [client.query],
  );

  // ─── Helpers ─────────────────────────────────────────────────────────

  const fetchOrders = useCallback<FetchOrders>(
    async (context) => {
      const { page, ordersPerPage } = context;

      if (!isLoggedIn) return { orders: [], lastPage: page };

      const { data } = await ordersQuery({ take: ordersPerPage, page });

      const ordersData = data?.ordersV2;
      const hasResolvedOrders =
        ordersData?.__typename === 'OrdersResponseSuccess';

      if (!hasResolvedOrders) {
        return {
          orders: [],
          lastPage: page,
          includeCanceledOrders: true,
          includePlacedOrders: true,
        };
      }

      // NOTE: BE pagination utilizes a zero-based counter (e.g., 0 == 1, 1 == 2, etc.),
      //       therefore, we increase the number by one to match the state machine model.
      const lastPage = (ordersData.pagination.last ?? 0) + 1;

      return { orders: ordersData.orders, lastPage };
    },
    [isLoggedIn, ordersQuery],
  );

  // ─────────────────────────────────────────────────────────────────────

  return useOrders<Order>({ fetchOrders });
};

// ─── Types ───────────────────────────────────────────────────────────────────

type FetchOrders = UseOrdersParams['fetchOrders'];

type UseOrdersParams = Parameters<typeof useOrders<Order>>[0];

type Order = Orders[number];

type Orders = NonNullable<
  Extract<
    OrdersV2ForReorderQuery['ordersV2'],
    { __typename: 'OrdersResponseSuccess' }
  >['orders']
>;
